/**
 * Domain normalization + validation.
 *
 * Used by: OnboardingWizard, AssetsPage, MonitoredAssetsTab, and
 * (via import) the server-side assetBillingEngine. Shared so that the
 * client-side gate and the server-side gate apply the exact same rule
 * — if the client lets a value through, so does the server, and vice
 * versa.
 *
 * Validation is a pure linear scan of character codes. No regex is used
 * for the shape check. This is deliberate:
 *   - eslint-plugin-security flagged the previous regex (star-height 2,
 *     overlapping char classes) as unsafe. It was — inputs like
 *     `aaaa...aaaaX` force catastrophic backtracking in JS's engine.
 *   - A hostname is a simple grammar; implementing it iteratively is
 *     trivial, obviously correct, and ReDoS-safe by construction.
 *   - This runs on every keystroke of the Onboarding domain input, so
 *     linear-bounded cost matters.
 */

/** Normalize user input to a bare hostname (lowercase, no protocol/path). */
export function normalizeDomain(raw: string): string {
  let s = raw.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.replace(/^www\./, '');
  const slash = s.indexOf('/');
  if (slash >= 0) s = s.slice(0, slash);
  return s.replace(/\.+$/, '').trim();
}

/**
 * Validate a hostname shape: at least two dot-separated labels, each
 * conforming to RFC 1035 (relaxed to allow all-numeric labels, matching
 * the prior regex's behavior).
 *
 * Linear O(n) scan, n bounded by 253. No regex.
 */
export function isValidDomain(hostname: string): boolean {
  const h = hostname.trim().toLowerCase();
  if (h.length < 3 || h.length > 253) return false;
  if (h.startsWith('.') || h.endsWith('.')) return false;
  if (h.includes('..')) return false;
  const labels = h.split('.');
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (!isValidDnsLabel(label)) return false;
  }
  return true;
}

/**
 * Single DNS label:
 *   - length 1..63
 *   - first and last chars are ASCII alphanumeric
 *   - inner chars are ASCII alphanumeric or `-`
 *
 * Kept non-exported: the only safe way to combine labels is through
 * `isValidDomain`, which enforces the surrounding `..`/start/end rules.
 */
function isValidDnsLabel(label: string): boolean {
  const n = label.length;
  if (n === 0 || n > 63) return false;
  if (!isAsciiAlnumLower(label.charCodeAt(0))) return false;
  if (n > 1 && !isAsciiAlnumLower(label.charCodeAt(n - 1))) return false;
  for (let i = 1; i < n - 1; i++) {
    const c = label.charCodeAt(i);
    if (!isAsciiAlnumLower(c) && c !== 45 /* '-' */) return false;
  }
  return true;
}

/**
 * ASCII `[a-z0-9]`. Input is lowercased by the caller, so this is the
 * complete alphanumeric test — no need to also accept A-Z.
 *   - 48..57  : 0-9
 *   - 97..122 : a-z
 */
function isAsciiAlnumLower(c: number): boolean {
  return (c >= 48 && c <= 57) || (c >= 97 && c <= 122);
}
